import CustomButton from "@/components/common/Button";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { filterByLabel } from "@/lib/filterByLabel";
import { DropdownService, TenantPermissionService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Select } from "antd";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PermissionModal, PermissionsTable } from "./components";
import { t } from "i18next";

const PermissionPage = () => {
  const [searchParams] = useSearchParams();
  const role = searchParams.get("role");
  const schema = searchParams.get("schema");
  const [selectedRole, setSelectedRole] = useState<string | null>(role ?? null);
  const [selectedSchema, setSelectedSchema] = useState<string | null>(
    schema ?? null
  );
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const { data: roleOptions, isLoading: roleLoading } = useQuery({
    queryKey: ["role"],
    queryFn: () => DropdownService.getDropdownData("TenantRole"),
  });

  const { data: schemaOptions, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema"],
    queryFn: () => DropdownService.getDropdownData("schema"),
  });

  const handleCancel = () => {
    setOpen(false);
  };

  const toolBarRender = () => {
    return [
      <CustomButton
        key="add"
        type="primary"
        onClick={() => setOpen(true)}
        title="Add Permission"
      >
        <Plus size={16} className="mr-1" /> {t("Add Permission")}
      </CustomButton>,
    ];
  };

  const {
    data: permissions,
    isLoading: permissionsLoading,
    refetch: refetchPermissions,
  } = useQuery<{
    results: any[];
  }>({
    queryKey: ["permissions", selectedRole, selectedSchema],
    queryFn: async () =>
      TenantPermissionService.getPermissionByRoleId(
        selectedRole ?? "",
        selectedSchema ?? ""
      ),
    enabled: !!selectedRole || !!selectedSchema,
  });

  if (permissionsLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title="Permission"
          onBack={() => navigate(navigationRoutes.USERS)}
        />
      }
    >
      <div className="mb-6 grid grid-cols-3 gap-4">
        <Select
          options={roleOptions}
          placeholder="Select Role"
          onChange={value => {
            setSelectedRole(value);
          }}
          value={selectedRole}
          allowClear
          loading={roleLoading}
          filterOption={filterByLabel}
        />
        <Select
          options={schemaOptions?.map((schema: any) => ({
            value: schema.label,
            label: schema.label,
          }))}
          placeholder="Select Schema"
          onChange={value => {
            setSelectedSchema(value);
          }}
          value={selectedSchema}
          allowClear
          loading={schemaLoading}
          filterOption={filterByLabel}
        />
      </div>

      <PermissionsTable
        permissions={permissions}
        permissionsLoading={permissionsLoading}
        roleOptions={roleOptions ?? []}
        toolBarRender={toolBarRender}
        schemaOptions={schemaOptions ?? []}
        refetchPermissions={refetchPermissions}
      />

      <PermissionModal
        open={open}
        onCancel={handleCancel}
        mode="create"
        refetchPermissions={refetchPermissions}
        selectedSchema={selectedSchema ?? ""}
        selectedRole={selectedRole ?? ""}
      />
    </Card>
  );
};

export default PermissionPage;
